import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Megaphone, Share2, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useApi } from "@/lib/useApi";
import { useT, Bi } from "@/lib/i18n";
import { dateLabel } from "@/lib/format";
import { Card, EmptyState, ErrorState, ListSkeleton, PageTitle } from "@/components/common";
import { Field, BigInput } from "@/components/form";
import { BackLink } from "@/pages/More";

export default function Announcements() {
  const { t, lang } = useT();
  const { property } = useAuth();
  const { data, loading, error, reload } = useApi<any[]>(property ? `/properties/${property.id}/announcements` : null);
  const [f, setF] = useState({ title: "", body: "" });
  const post = async () => {
    try {
      await api.post(`/properties/${property.id}/announcements`, f);
      setF({ title: "", body: "" });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (id: string) => {
    if (!window.confirm(t("delete") + "?")) return;
    await api.delete(`/announcements/${id}`);
    reload();
  };
  return (
    <div>
      <BackLink />
      <PageTitle title={t("announcements")} sub={property?.name} />
      <Card className="p-4 space-y-3 mb-5" data-testid="announcement-form">
        <p className="font-bold"><Bi k="new_announcement" /></p>
        <Field label={t("title")}><BigInput data-testid="announcement-title-input" maxLength={100} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label={t("message")}><Textarea data-testid="announcement-body-input" maxLength={1000} rows={3} className="rounded-xl text-base bg-white" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /></Field>
        <Button data-testid="announcement-post-btn" disabled={!f.title.trim()} onClick={post} className="w-full h-12 rounded-xl bg-emerald-700 hover:bg-emerald-800">{t("post")}</Button>
      </Card>
      {loading && !data ? <ListSkeleton rows={2} /> : error ? <ErrorState message={error} onRetry={reload} /> : !data.length ? (
        <EmptyState testId="announcements-empty" icon={Megaphone} title={t("no_announcements")} />
      ) : (
        <div className="space-y-2.5 pg-stagger">
          {data.map((a) => (
            <Card key={a.id} className="p-4" data-testid={`announcement-${a.id}`}>
              <div className="flex justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold">{a.title}</p>
                  <p className="text-xs text-slate-400">{dateLabel(a.created_at, lang)}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <a data-testid={`announcement-share-${a.id}`} href={`https://wa.me/?text=${encodeURIComponent(`*${property.name}*\n${a.title}\n${a.body}`)}`} target="_blank" rel="noreferrer" className="h-10 w-10 grid place-items-center rounded-full text-[#128C4A] hover:bg-emerald-50"><Share2 className="h-4 w-4" /></a>
                  <button data-testid={`announcement-delete-${a.id}`} onClick={() => remove(a.id)} className="h-10 w-10 grid place-items-center rounded-full text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {a.body && <p className="text-sm text-slate-600 mt-2 whitespace-pre-line">{a.body}</p>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
